// api.ts
import { TokenType, getDecryptedJwtAndType } from "../utils/tokenEncryptionUtils";
import { getLanguage } from "../utils/languageUtils";

export class HttpError extends Error {
  readonly status: number;
  readonly statusText?: string;

  constructor(status: number, statusText?: string, message?: string) {
    super(message || statusText);
    this.name = "HttpError";
    this.status = status;
    this.statusText = statusText;
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  static async fromResponse(res: Response): Promise<HttpError> {
    let message: string | undefined;
    try {
      const body = await res.json();
      message = body?.detail;
    } catch {
      // ignore parse errors
    }
    return new HttpError(res.status, res.statusText, message || res.statusText);
  }
}

export const isHttpError = (err: unknown): err is HttpError => err instanceof HttpError;

interface ApiOptions {
  responseType?: "json" | "arraybuffer";
  keepalive?: boolean;
}

export const getAuthHeaderValue = (
  jwt: string | null,
  tokenType: TokenType
): string => {
  if (!jwt) return "";
  switch (tokenType) {
    case TokenType.STYTCH:
      return `Bearer ${jwt}`;
    case TokenType.JOIN:
      return `Join ${jwt}`;
    default:
      return "";
  }
};

export const api = async (
  url_extension: string,
  method: string,
  base?: string,
  data?: any,
  options?: ApiOptions,
  performLogging: boolean = true,
  requireAuth: boolean = true
): Promise<any> => {
  const [jwt, tokenType] = getDecryptedJwtAndType();
  const authHeaderValue = getAuthHeaderValue(jwt || null, tokenType);

  // Check if there's a language query parameter to override Accept-Language
  const languageParam = getLanguage();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  // Only override Accept-Language if query parameter is present
  if (languageParam) {
    headers["Accept-Language"] = languageParam;
  }
  // Otherwise, don't set it - let fetch use browser's default Accept-Language header

  if (jwt) {
    headers["Authorization"] = authHeaderValue;
  } else {
    if (requireAuth) {
      console.info("No JWT provided, but authentication is required.", url_extension);
      //throw new Error("No JWT provided");
      //return;
    }
  }

  let urlBase = base || import.meta.env.VITE_BASE_BACKEND_URL;
  if (performLogging) {
    console.log("-> ENDPOINT: ", urlBase + url_extension, method, " DATA:", data, " JWT:", jwt?.length || 0, "bytes HEADERS:", headers);
  }
  const response = await fetch(urlBase + url_extension, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    keepalive: options?.keepalive,
    credentials: "include", // include cookies in requests; could also be 'same-origin' or 'omit'
  });

  if (!response.ok) {
    console.error("API error:", response, response.status, response.statusText);
    throw await HttpError.fromResponse(response);
  }

  if (options?.responseType === "arraybuffer") {
    return response.arrayBuffer();
  }

  return response.json();
};
